import {
  Children,
  CSSProperties,
  PropsWithChildren,
  type Ref,
  isValidElement,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MenuChangeEvent } from "@szhsin/react-menu";
import "./combobox.scss";
import { FormInputElement } from "@archetypes/form-element";
import { LabelInfo } from "@components/label-info";
import { FormError } from "@components/form-error";
import { Button, type ButtonVariant } from "@components/button";
import { useDebounceValue } from "usehooks-ts";
import { matchSorter } from "match-sorter";
import { Action } from "@components/action/action";
import { isNullOrUndefined } from "@utils/data-type.util";
import { FIELD_EVENT_DEBOUNCE_TIME } from "@constants/form";
import { CloseIcon } from "@components/icons";
import { CustomTooltip } from "@components/tool-tip";

export interface ComboBoxOption<T> extends PropsWithChildren {
  value: T;
  className?: string;
  searchValue?: string;
  checked?: boolean;
  onClick?: (value: T) => void;
  disabled?: boolean;
  checkedVariant?: ButtonVariant;
  checkedClassName?: string;
  checkedStyle?: CSSProperties;
  optionRef?: Ref<HTMLDivElement>;
  tooltipContent?: string;
}

const OPTION_TOOLTIP_MAX_WIDTH = 320;
// The dropdown sits above most of the app, so the tooltip has to clear it.
const OPTION_TOOLTIP_Z_INDEX = 99999;

export const COption = <T,>({
  value,
  className,
  children,
  onClick,
  checked,
  disabled = false,
  checkedVariant = "primary",
  checkedClassName,
  checkedStyle,
  optionRef,
  tooltipContent,
}: ComboBoxOption<T>) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  useLayoutEffect(() => {
    const label = labelRef.current;
    if (!label || !tooltipContent) {
      return;
    }
    const checkTruncation = () => {
      const truncated = label.scrollWidth > label.clientWidth;
      setIsTruncated((prevTruncated) => (prevTruncated === truncated ? prevTruncated : truncated));
    };
    // Measure up front so the tooltip is armed before the first hover, then keep
    // it in sync as the dropdown resizes.
    checkTruncation();
    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(label);
    return () => resizeObserver.disconnect();
  }, [tooltipContent]);

  return (
    <div className="w-100" ref={optionRef}>
      <Button
        disabled={disabled}
        onClick={() => onClick?.(value)}
        variant={checked ? checkedVariant : "default"}
        className={`${className ?? ""} ${checked ? (checkedClassName ?? "") : ""} justify-start no-radius`}
        style={checked ? checkedStyle : undefined}
      >
        {tooltipContent ? (
          <CustomTooltip
            // Only truncated labels need the tooltip; an empty content keeps it
            // mounted but silent.
            content={isTruncated ? tooltipContent : undefined}
            direction="top"
            maxWidth={OPTION_TOOLTIP_MAX_WIDTH}
            zIndex={OPTION_TOOLTIP_Z_INDEX}
            className="select-option-tooltip"
            wrapperClass="select-option-tooltip-wrapper"
          >
            <span className="select-option-label" ref={labelRef}>
              {children}
            </span>
          </CustomTooltip>
        ) : (
          children
        )}
      </Button>
    </div>
  );
};

interface ComboboxProps<T> extends FormInputElement {
  onChange?: (value?: T) => void;
  value?: T;
  getKey: (value: T) => string | number;
  renderValue: (value: T) => string | undefined;
  searchKeys?: string[];
  errorMessage?: string;
  containerClassName?: string;
  labelClassName?: string;
  allowDeselect?: boolean;
  infoText?: string;
  keepOpenOnClick?: boolean;
  zIndex?: string;
  optionColorMode?: "brand" | "app";
}

const getScrollableParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

const ComboboxComponent = <T,>({
  value,
  onChange,
  placeholder = "Select items",
  getKey,
  renderValue,
  searchKeys = [],
  label,
  errorMessage,
  containerClassName,
  labelClassName,
  required,
  infoText,
  children,
  allowDeselect = false,
  disabled,
  keepOpenOnClick,
  zIndex,
  optionColorMode = "brand",
}: ComboboxProps<T>) => {
  const arrayOfChildren = useMemo(() => {
    return Children.toArray(children);
  }, [children]);

  const valueKey = useMemo(() => (value ? getKey(value) : undefined), [getKey, value]);
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedFromDropdown, setSelectedFromDropdown] = useState<boolean>(false);

  const [debouncedValue] = useDebounceValue<string>(inputValue, FIELD_EVENT_DEBOUNCE_TIME);

  useEffect(() => {
    if (value) {
      setInputValue(renderValue(value) ?? "");
      setSelectedFromDropdown(true);
    } else {
      setInputValue("");
      setSelectedFromDropdown(false);
    }
  }, [renderValue, value]);

  const onOptionClick = useCallback(
    (value?: T) => {
      onChange?.(value);
    },
    [onChange],
  );

  const hideOption = useCallback(
    (optionValue: T) => {
      if (
        !selectedFromDropdown &&
        !matchSorter(
          [optionValue],
          debouncedValue,
          Array.isArray(searchKeys) && searchKeys.length ? { keys: searchKeys } : undefined,
        ).length
      ) {
        return true;
      }
      return false;
    },
    [debouncedValue, searchKeys, selectedFromDropdown],
  );

  const removeItem = useCallback(() => {
    onChange?.(undefined);
  }, [onChange]);

  const selectedOptionRef = useRef<HTMLDivElement>(null);

  const onMenuChange = useCallback(({ open }: MenuChangeEvent) => {
    if (!open) {
      return;
    }
    // The dropdown mounts and gets positioned as it opens, so wait for the next
    // frame before measuring where the selected option ended up.
    requestAnimationFrame(() => {
      const option = selectedOptionRef.current;
      const dropdown = option ? getScrollableParent(option) : null;
      if (!option || !dropdown) {
        return;
      }
      const optionRect = option.getBoundingClientRect();
      const dropdownRect = dropdown.getBoundingClientRect();
      const isFullyVisible =
        optionRect.top >= dropdownRect.top && optionRect.bottom <= dropdownRect.bottom;
      if (isFullyVisible) {
        return;
      }
      // Centre the selected option in the dropdown; the browser clamps this to
      // the scrollable range, so options near either end just sit at the edge.
      dropdown.scrollTop +=
        optionRect.top - dropdownRect.top - (dropdown.clientHeight - optionRect.height) / 2;
    });
  }, []);

  const selectedOptionStyle = useMemo<CSSProperties>(() => {
    if (optionColorMode === "app") {
      return {
        "--combobox-option-selected-bg": "color-mix(in srgb, var(--ul-app-primary) 16%, white)",
        "--combobox-option-selected-color": "var(--ul-app-primary)",
        "--combobox-option-hover-bg": "var(--ul-app-primary)",
        "--combobox-option-hover-color": "var(--ul-app-font)",
      } as CSSProperties;
    }

    return {
      "--combobox-option-selected-bg": "var(--color-brand-light)",
      "--combobox-option-selected-color": "var(--color-brand)",
      "--combobox-option-hover-bg": "var(--color-brand)",
      "--combobox-option-hover-color": "#ffffff",
    } as CSSProperties;
  }, [optionColorMode]);

  return (
    <div className={`select-wrapper ${containerClassName}`}>
      <LabelInfo
        label={label}
        labelClassName={labelClassName}
        required={required}
        infoText={infoText}
      />
      <div className="d-flex relative">
        <Action
          zIndex={zIndex}
          label={!isNullOrUndefined(value) ? <>{renderValue(value as T)}</> : placeholder}
          mode="select"
          disabled={disabled}
          keepOpenOnClick={keepOpenOnClick}
          containerClassName="mx-h-200"
          onMenuChange={onMenuChange}
        >
          {arrayOfChildren.map((child) => {
            if (isValidElement<ComboBoxOption<T>>(child)) {
              const optionValue = child.props.value as T;
              if (hideOption(optionValue)) {
                return null;
              }
              const key = getKey(optionValue);
              const isSelected = key === valueKey;
              return (
                <COption
                  {...child.props}
                  key={key}
                  onClick={onOptionClick}
                  className="w-100 combobox-option"
                  tooltipContent={renderValue(optionValue)}
                  optionRef={isSelected ? selectedOptionRef : undefined}
                  checked={isSelected}
                  checkedVariant="default"
                  checkedClassName="combobox-option-selected"
                  checkedStyle={selectedOptionStyle}
                >
                  {child.props.children}
                </COption>
              );
            }
            return null;
          })}
        </Action>
        {allowDeselect && !isNullOrUndefined(value) && (
          <Button
            variant="transparent"
            className="absolute t-0 r-50 p-1 m-0 overflow-visible"
            onClick={removeItem}
          >
            <CloseIcon color="#292D32" size={11} />
          </Button>
        )}
      </div>

      <FormError errorMessage={errorMessage} />
    </div>
  );
};

export const Combobox = memo(ComboboxComponent) as typeof ComboboxComponent;
